import "./index.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "@arcgis/core/assets/esri/themes/light/main.css";

import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { configureStore } from "@reduxjs/toolkit";
import axios from "axios";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";

import App from "./App";
import { msalConfig, protectedResources } from "./authConfig";
import ErrorBoundary from "./components/templates/Errors/error-boundary";
import ConfirmContextProvider from "./components/UI/organisms/ConfirmDialog/confirm-context-provider";
import ConfirmDialog from "./components/UI/organisms/ConfirmDialog/confirm-dialog";
import reportWebVitals from "./reportWebVitals";
import { reduxReducer } from "./store/store";
import { setAuthInteractionRequired, getAndClearAuthInteractionRequired, onAuthInteractionRequired } from "./msalAuthBridge";

// --- Redux store ---
export const store = configureStore({
  reducer: reduxReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

TimeAgo.addDefaultLocale(en);

// --- MSAL instance (MUST be created once) ---
export const msalInstance = new PublicClientApplication(msalConfig);

// --- MSAL event logging + set active account on success ---
msalInstance.addEventCallback((e) => {
  console.log("[MSAL EVENT]", e.eventType, e);

  if (e.eventType === EventType.LOGIN_SUCCESS && e.payload && "account" in e.payload) {
    const { account } = e.payload as any;
    msalInstance.setActiveAccount(account);
    console.log("[MSAL] Active account set:", account);
  }

  if (e.eventType === EventType.HANDLE_REDIRECT_START) console.log("➡️ HANDLE_REDIRECT_START");
  if (e.eventType === EventType.HANDLE_REDIRECT_END) console.log("⬅️ HANDLE_REDIRECT_END");
  if (e.eventType === EventType.LOGIN_FAILURE) console.log("❌ LOGIN_FAILURE", e.error);
  if (e.eventType === EventType.ACQUIRE_TOKEN_FAILURE) console.log("❌ ACQUIRE_TOKEN_FAILURE", e.error);
});

// --- Axios interceptor: silent refresh ONLY (no redirect here) ---
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If we have no response it's network/CORS/etc, not an auth status code
    if (!error?.response) return Promise.reject(error);

    const status = error.response.status;

    // Only handle auth-related statuses
    if (status !== 401 && status !== 403) return Promise.reject(error);

    const originalRequest = error.config;
    originalRequest.requestRetry = (originalRequest.requestRetry ?? 0) + 1;
    if (originalRequest.requestRetry > 1) return Promise.reject(error);

    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) {
      // Not logged in; let AuthGate drive login
      setAuthInteractionRequired({
        reason: "no_account",
        scopes: protectedResources.appServiceApi.scopes,
      });
      return Promise.reject(error);
    }

    const tokenRequest = { account, scopes: protectedResources.appServiceApi.scopes };

    try {
      const resp = await msalInstance.acquireTokenSilent(tokenRequest);
      axios.defaults.headers.common.Authorization = `Bearer ${resp.accessToken}`;
      originalRequest.headers.Authorization = `Bearer ${resp.accessToken}`;
      return axios(originalRequest);
    } catch (e: any) {
      // Don't redirect here — signal UI to do it once
      setAuthInteractionRequired({
        reason: "interaction_required",
        scopes: protectedResources.appServiceApi.scopes,
      });
      return Promise.reject(e);
    }
  }
);

// --- Root ---
const container = document.getElementById("root");
if (!container) throw new Error("Root element '#root' not found");

createRoot(container).render(

    <MsalProvider instance={msalInstance}>
      <Provider store={store}>
        <ErrorBoundary>
          <ConfirmContextProvider>
            {/* AuthGate is where redirect happens */}
            <AuthGate>
              <App />
            </AuthGate>
            <ConfirmDialog />
          </ConfirmContextProvider>
        </ErrorBoundary>
      </Provider>
    </MsalProvider>

);

reportWebVitals();

// --- AuthGate: the ONLY place we initiate redirects ---
import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

function AuthGate({ children }: { children: React.ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [interactionSignal, setInteractionSignal] = React.useState(0);

  // Subscribe to auth-interaction-required signals so the effect re-runs
  // even when inProgress/isAuthenticated are stable after initial login.
  React.useEffect(() => {
    return onAuthInteractionRequired(() => {
      setInteractionSignal((n) => n + 1);
    });
  }, []);

  React.useEffect(() => {
    // Let MSAL finish processing redirects before we do anything
    if (inProgress !== InteractionStatus.None) return;

    // If axios/token flow signaled interaction required, do it ONCE here
    const request = getAndClearAuthInteractionRequired();
    if (request) {
      instance.acquireTokenRedirect({ scopes: request.scopes });
      return;
    }

    // If not authenticated, kick off login
    if (!isAuthenticated) {
      instance.loginRedirect({ scopes: ["User.Read"] });
    }
  }, [inProgress, isAuthenticated, instance, interactionSignal]);

  if (!isAuthenticated) return null; // or spinner
  return <>{children}</>;
}
